import type {
  CtaBannerBgStyle,
  CtaBannerButtonVariant,
  CtaBannerLayout,
  CtaBannerProps,
  CtaBannerSize,
} from "@emkit/builder-widgets";
import { useEditor } from "../../store.js";
import { ColorField, NumberField, SelectField, TextField } from "./fields.js";
import type { WidgetInspectorProps } from "./registry.js";

const LAYOUT = ["centered", "split"] as const;
const SIZE = ["sm", "md", "lg"] as const;
const BG = ["solid", "gradient"] as const;
const VARIANT = ["primary", "secondary", "ghost"] as const;

export function CtaBannerInspector({ widget }: WidgetInspectorProps) {
  const update = useEditor((s) => s.updateWidgetProps);
  const p = widget.props as CtaBannerProps;

  return (
    <>
      <SelectField
        label="Layout"
        value={p.layout ?? "centered"}
        options={LAYOUT}
        onChange={(v) => update(widget.id, { layout: v as CtaBannerLayout })}
      />
      <SelectField
        label="Size"
        value={p.size ?? "md"}
        options={SIZE}
        onChange={(v) => update(widget.id, { size: v as CtaBannerSize })}
      />
      <TextField
        label="Title"
        value={p.title}
        onChange={(v) => update(widget.id, { title: v })}
      />
      <TextField
        label="Subtitle"
        value={p.subtitle ?? ""}
        multiline
        onChange={(v) => update(widget.id, { subtitle: v || undefined })}
      />

      <div className="emke-section-title" style={{ marginTop: 8 }}>
        Primary CTA
      </div>
      <TextField
        label="Label"
        value={p.primaryCtaLabel}
        onChange={(v) => update(widget.id, { primaryCtaLabel: v })}
      />
      <TextField
        label="URL"
        value={p.primaryCtaHref}
        onChange={(v) => update(widget.id, { primaryCtaHref: v })}
      />
      <SelectField
        label="Style"
        value={p.primaryCtaVariant ?? "primary"}
        options={VARIANT}
        onChange={(v) =>
          update(widget.id, { primaryCtaVariant: v as CtaBannerButtonVariant })
        }
      />

      <div className="emke-section-title" style={{ marginTop: 8 }}>
        Secondary CTA
      </div>
      <TextField
        label="Label"
        value={p.secondaryCtaLabel ?? ""}
        onChange={(v) => update(widget.id, { secondaryCtaLabel: v || undefined })}
      />
      <TextField
        label="URL"
        value={p.secondaryCtaHref ?? ""}
        onChange={(v) => update(widget.id, { secondaryCtaHref: v || undefined })}
      />
      <SelectField
        label="Style"
        value={p.secondaryCtaVariant ?? "ghost"}
        options={VARIANT}
        onChange={(v) =>
          update(widget.id, {
            secondaryCtaVariant: v as CtaBannerButtonVariant,
          })
        }
      />

      <div className="emke-section-title" style={{ marginTop: 8 }}>
        Background
      </div>
      <SelectField
        label="Style"
        value={p.bgStyle ?? "solid"}
        options={BG}
        onChange={(v) => update(widget.id, { bgStyle: v as CtaBannerBgStyle })}
      />
      <ColorField
        label="Background"
        value={p.background ?? "#111827"}
        onChange={(v) => update(widget.id, { background: v })}
      />
      {(p.bgStyle ?? "solid") === "gradient" && (
        <ColorField
          label="Background end"
          value={p.backgroundEnd ?? "#374151"}
          onChange={(v) => update(widget.id, { backgroundEnd: v })}
        />
      )}
      <ColorField
        label="Text"
        value={p.textColor ?? "#ffffff"}
        onChange={(v) => update(widget.id, { textColor: v })}
      />
      <ColorField
        label="Accent (primary btn)"
        value={p.accent ?? "#ffffff"}
        onChange={(v) => update(widget.id, { accent: v })}
      />
      <NumberField
        label="Corner radius (px)"
        value={p.radius ?? 16}
        min={0}
        max={64}
        onChange={(v) => update(widget.id, { radius: v })}
      />
    </>
  );
}
